// Pure helpers for the task recurrence UI value <-> backend `recurrence` payload.
// Contract: zofino-strapi/docs/tasks/05-recurring-tasks-design.md

export const RECURRENCE_FREQUENCIES = ["none", "daily", "weekly", "monthly", "yearly"];
export const COMPLETION_UNITS = ["hour", "day", "week", "month"];
export const MONTHLY_MODES = ["dayOfMonth", "nthWeekday", "lastDay"];
export const END_MODES = ["never", "untilDate", "count"];

export function createDefaultRecurrence() {
    return {
        frequency: "none",
        interval: 1,
        weekDays: [],
        recurrenceMode: "schedule",
        completionUnit: "day",
        monthlyMode: "dayOfMonth",
        nthWeek: 1,
        nthWeekDay: null,
        missingDayPolicy: "lastDay",
        endMode: "never",
        endDate: null,
        occurrences: null,
    };
}

export function isRecurring(value) {
    return Boolean(value) && value.frequency !== "none";
}

/** Validation messages (translation keys) for a UI value. Empty array = valid. */
export function recurrenceErrors(value) {
    const errors = [];
    if (!isRecurring(value)) return errors;
    if (!Number.isInteger(Number(value.interval)) || Number(value.interval) < 1) {
        errors.push("date-picker.recurrence.interval-invalid");
    }
    if (value.recurrenceMode !== "completion") {
        if (value.frequency === "weekly" && (!value.weekDays || value.weekDays.length === 0)) {
            errors.push("date-picker.recurrence.week-day-required");
        }
        if ((value.frequency === "monthly" || value.frequency === "yearly") && value.monthlyMode === "nthWeekday"
            && (value.nthWeekDay === null || value.nthWeekDay === undefined)) {
            errors.push("date-picker.recurrence.weekday-required");
        }
    }
    if (value.endMode === "untilDate" && !value.endDate) errors.push("date-picker.recurrence.end-date-required");
    if (value.endMode === "count" && !(Number(value.occurrences) >= 1)) {
        errors.push("date-picker.recurrence.occurrences-required");
    }
    return errors;
}

/** Backend payload for `data.recurrence`, or null when the task does not repeat. */
export function toRecurrencePayload(value) {
    if (!isRecurring(value)) return null;
    const completion = value.recurrenceMode === "completion";
    const payload = {
        recurrenceMode: completion ? "completion" : "schedule",
        interval: Math.max(1, Number(value.interval) || 1),
        endType: value.endMode === "untilDate" ? "untilDate" : value.endMode === "count" ? "count" : "never",
    };
    if (completion) {
        payload.frequency = "daily";
        payload.completionUnit = COMPLETION_UNITS.includes(value.completionUnit) ? value.completionUnit : "day";
    } else {
        payload.frequency = value.frequency;
        if (value.frequency === "weekly") payload.weekDays = [...(value.weekDays || [])].sort();
        if (value.frequency === "monthly" || value.frequency === "yearly") {
            payload.monthlyMode = value.monthlyMode || "dayOfMonth";
            if (payload.monthlyMode === "nthWeekday") {
                payload.nthWeek = Number(value.nthWeek);
                payload.nthWeekDay = Number(value.nthWeekDay);
            }
            payload.missingDayPolicy = value.missingDayPolicy || "lastDay";
        }
    }
    if (payload.endType === "untilDate") payload.endDate = value.endDate;
    if (payload.endType === "count") payload.occurrenceLimit = Number(value.occurrences);
    return payload;
}

/** UI value from the `recurrence` summary the API returns on tasks (series rule). */
export function recurrenceFromSeries(series) {
    if (!series || !series.frequency) return createDefaultRecurrence();
    const completion = series.recurrenceMode === "completion";
    return {
        ...createDefaultRecurrence(),
        frequency: series.frequency,
        interval: series.interval || 1,
        weekDays: Array.isArray(series.weekDays) ? series.weekDays : [],
        recurrenceMode: completion ? "completion" : "schedule",
        completionUnit: series.completionUnit || "day",
        monthlyMode: series.monthlyMode || "dayOfMonth",
        nthWeek: series.nthWeek ?? 1,
        nthWeekDay: series.nthWeekDay ?? null,
        missingDayPolicy: series.missingDayPolicy || "lastDay",
        endMode: series.endType === "untilDate" ? "untilDate" : series.endType === "count" ? "count" : "never",
        endDate: series.endDate || null,
        occurrences: series.occurrenceLimit ?? null,
    };
}

/** True when two UI values describe the same rule (ignores nothing: used to detect a rule edit). */
export function sameRecurrence(a, b) {
    return JSON.stringify(toRecurrencePayload(a)) === JSON.stringify(toRecurrencePayload(b));
}

/** Local time of day ("HH:mm", Asia/Tehran fallback handled by the browser tz) of a unix-seconds or ISO value. */
export function timeOfDay(value) {
    if (!value) return null;
    const date = typeof value === "number" ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Asia/Tehran",
    }).format(date);
}

/**
 * Short localized summary, e.g. "هر روز · 22:00". `translate` is the app translate function.
 * `series` is the API `recurrence` summary of a task (or a UI value built with recurrenceFromSeries).
 */
export function recurrenceSummary(series, translate, dueAt = null) {
    if (!series || (series.frequency === undefined && series.recurrenceMode === undefined)) return "";
    const value = series.recurrenceMode !== undefined && series.recurrenceMode === "completion" ? series : series;
    const time = timeOfDay(dueAt || series.anchorDueAt);
    let text;
    if (value.recurrenceMode === "completion") {
        const unit = translate(`date-picker.recurrence.completion-unit.${value.completionUnit || "day"}`);
        text = `${translate("date-picker.recurrence.after-completion")} ${value.interval || 1} ${unit}`;
    } else {
        const interval = Number(value.interval || 1);
        const base = translate(`date-picker.recurrence.${value.frequency}`);
        text = interval > 1
            ? `${translate("date-picker.recurrence.every")} ${interval} ${translate(`date-picker.recurrence.unit.${value.frequency}`)}`
            : base;
    }
    return time && value.recurrenceMode !== "completion" ? `${text} · ${time}` : text;
}
